import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  palette,
  useSnackbar,
} from '@munifocus/ui';
import {
  HANDOVER_FILE_NOTE_MAX,
  HANDOVER_NOTE_MAX,
  MAX_HANDOVER_FILES,
} from '@munifocus/contracts-gov';
import type { FileSummary } from '@munifocus/contracts-gov';
import { useFiles } from '../../lib/files-api';
import { useCreateHandover, useHandoverCandidates } from '../../lib/teamwork-api';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { useOptionalScope } from '../../providers/ScopeProvider';
import { toLocalInput } from './handover-display';

type FileOption = Pick<FileSummary, 'id' | 'reference' | 'address'>;

export interface WriteHandoverDialogProps {
  open: boolean;
  onClose: () => void;
  /** Files to start with — the file the dialog was opened from. */
  initialFiles?: FileOption[];
  onCreated?: (id: string) => void;
}

/**
 * Write a shift handover: the period, a note, optionally the files it is
 * about (with a line each), and the colleague it is for.
 *
 * The recipient list is the server's answer to "who can read every one of
 * these files" — a handover is never a way to show someone a file they could
 * not otherwise open. With no files, it is anyone working at the active
 * municipality.
 */
export function WriteHandoverDialog({
  open,
  onClose,
  initialFiles = [],
  onCreated,
}: WriteHandoverDialogProps) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const scope = useOptionalScope();
  const files = useFiles();
  const create = useCreateHandover();

  const [selected, setSelected] = useState<FileOption[]>(initialFiles);
  const [fileNotes, setFileNotes] = useState<Record<string, string>>({});
  const [start, setStart] = useState(() => toLocalInput(new Date()));
  const [end, setEnd] = useState(() => toLocalInput(new Date(Date.now() + 12 * 3_600_000)));
  const [note, setNote] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(initialFiles);
    setFileNotes({});
    setStart(toLocalInput(new Date()));
    setEnd(toLocalInput(new Date(Date.now() + 12 * 3_600_000)));
    setNote('');
    setToUserId('');
    setError(null);
    // Reset per opening only; `initialFiles` is a fresh array on every render.
  }, [open]);

  const lgId =
    scope?.active?.kind === 'local_government' ? scope.active.id : (scope?.active?.id ?? null);
  const fileIds = useMemo(() => selected.map((f) => f.id), [selected]);
  const candidates = useHandoverCandidates(open ? fileIds : [], open ? lgId : null);

  useEffect(() => {
    if (toUserId && candidates.data && !candidates.data.some((c) => c.userId === toUserId)) {
      setToUserId('');
    }
  }, [candidates.data, toUserId]);

  const options: FileOption[] = (files.data?.items ?? []).filter((f) => f.status !== 'closed');
  const startDate = new Date(start);
  const endDate = new Date(end);
  const periodValid =
    !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && endDate > startDate;
  const canSubmit = Boolean(note.trim()) && Boolean(toUserId) && periodValid && !create.isPending;

  const submit = () => {
    if (!canSubmit) return;
    setError(null);
    create.mutate(
      {
        toUserId,
        periodStart: startDate.toISOString(),
        periodEnd: endDate.toISOString(),
        note: note.trim(),
        ...(fileIds.length ? { fileIds, fileNotes } : { localGovernmentId: lgId ?? undefined }),
      },
      {
        onSuccess: (handover) => {
          snackbar.success(t('gov.teamwork.handovers.sent'));
          onCreated?.(handover.id);
          onClose();
        },
        onError: (err) => setError(apiErrorMessage(err, t('gov.teamwork.handovers.sendError'))),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.teamwork.handovers.writeTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: palette.text.muted }}>
            {t('gov.teamwork.handovers.writeHint')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('gov.teamwork.handovers.start')}
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label={t('gov.teamwork.handovers.end')}
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              fullWidth
              error={!periodValid}
              helperText={periodValid ? undefined : t('gov.teamwork.handovers.periodError')}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          <Autocomplete
            multiple
            options={options}
            value={selected}
            loading={files.isLoading}
            onChange={(_, value) => setSelected(value.slice(0, MAX_HANDOVER_FILES))}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={(f) => `${f.reference} — ${f.address}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('gov.teamwork.handovers.files')}
                helperText={t('gov.teamwork.handovers.filesHint')}
              />
            )}
          />

          {selected.map((file) => (
            <TextField
              key={file.id}
              size="small"
              label={t('gov.teamwork.handovers.fileNote', { reference: file.reference })}
              value={fileNotes[file.id] ?? ''}
              onChange={(e) =>
                setFileNotes((prev) => ({
                  ...prev,
                  [file.id]: e.target.value.slice(0, HANDOVER_FILE_NOTE_MAX),
                }))
              }
            />
          ))}

          <TextField
            label={t('gov.teamwork.handovers.note')}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, HANDOVER_NOTE_MAX))}
            multiline
            minRows={4}
            required
          />

          <TextField
            select
            label={t('gov.teamwork.handovers.to')}
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
            disabled={!candidates.data?.length}
            helperText={
              candidates.data && candidates.data.length === 0
                ? t('gov.teamwork.handovers.noCandidates')
                : t('gov.teamwork.handovers.toHint')
            }
            slotProps={{ htmlInput: { 'data-testid': 'handover-recipient' } }}
          >
            {(candidates.data ?? []).map((person) => (
              <MenuItem key={person.userId} value={person.userId}>
                {person.displayName}
              </MenuItem>
            ))}
          </TextField>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          {t('gov.teamwork.handovers.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
